import React, { useState } from 'react';
import { Check, X } from 'lucide-react';

interface StreamRenameRowProps {
  /** Display form only — never the numeric prefix (that's structural, never user-edited). */
  initialValue: string;
  onConfirm: (newBareName: string) => void;
  onCancel: () => void;
  busy?: boolean;
}

/**
 * Inline rename editor rendered in place of a stream's section-header title
 * while that stream is being renamed. Visually identical to the existing
 * "+ New Stream" inline input in ContentCatalogue.tsx (same
 * .new-stream-input-row / .new-stream-input / .new-stream-confirm /
 * .new-stream-cancel classes, defined in ContentCatalogue.css) — reused
 * rather than forked, per the feature spec.
 */
const StreamRenameRow: React.FC<StreamRenameRowProps> = ({ initialValue, onConfirm, onCancel, busy = false }) => {
  const [value, setValue] = useState(initialValue);

  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed || busy) return;
    onConfirm(trimmed);
  };

  return (
    <div
      className="new-stream-input-row stream-rename-row"
      onClick={(e) => e.stopPropagation()}
    >
      <input
        className="new-stream-input"
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.stopPropagation();
            submit();
          }
          if (e.key === 'Escape') {
            e.stopPropagation();
            onCancel();
          }
        }}
        // CollapsibleSection's section-header toggle listens on onKeyPress
        // (a distinct synthetic event from onKeyDown above) — stopping only
        // keydown does not stop the browser's separate keypress dispatch, so
        // Enter here would still bubble up and toggle the section open/closed.
        onKeyPress={(e) => e.stopPropagation()}
        disabled={busy}
        autoFocus
      />
      <button className="new-stream-confirm" onClick={submit} disabled={busy} title="Rename">
        <Check size={14} />
      </button>
      <button className="new-stream-cancel" onClick={onCancel} disabled={busy} title="Cancel">
        <X size={14} />
      </button>
    </div>
  );
};

export default StreamRenameRow;
